import React, { useState, useEffect, useRef } from 'react';
import { Canvas } from '@react-three/fiber';
import Scribble from '../../Utils/Scribble.js';
import SceneComponent from './SceneComponent';
import { ModelListBR } from "../../Utils/ModelsList.js"
import LayerControl from './LayerControl'; // Import LayerControl component
import Measurements from '../../clickTools/Measurements.js';
import Info from '../../clickTools/Info.js';
import ColorChange from '../../clickTools/ColorChange.js';
import ObjectDetails from '../../clickTools/ObjectDetails.js';
import { useProgress } from '@react-three/drei';
import StopeStatistics from './StopeStatistics.js';
import ObjectDetailsPage from './ObjectDetailsPage.js';
import { SceneSlicer } from '../SceneSlicer/index.jsx';
import {slides} from '../../Utils/Presentations.js'
import BottomBarContainer from './BottomBarContainer.js';
import BlockModelLegend from './BlockModelLegend.js';


const Presentation = () => {

  const [three, setThree] = useState(null)
  
  const { progress } = useProgress()

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isScribbleMode, setIsScribbleMode] = useState(false);
  const [selectedColor, setSelectedColor] = useState('black');
  const [previousSlideWasScene, setPreviousSlideWasScene] = useState(false);
  const [showDescription, setShowDescription] = useState(true); // Track description visibility

  // Automatically close sidebar on medium screens and below
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) { // Adjust the breakpoint as needed
        setIsSidebarOpen(false);
      }
    };

    // Initial check and add event listener
    handleResize();
    window.addEventListener('resize', handleResize);

    // Clean up the event listener on unmount
    return () => window.removeEventListener('resize', handleResize);
  }, []);



  useEffect(()=>{
    console.log('presentation refreshed')
  },
    [])


  

  const handleSlideClick = (index) => {
    setPreviousSlideWasScene(slides[currentSlide].type === 'scene');
    setCurrentSlide(index);
    setIsScribbleMode(false); // Disable scribble mode on page change
    
    setShowDescription(true)
    


    const newVisibility = { ...layerVisibility };
    
    // Reset all layers to invisible
    Object.keys(newVisibility).forEach((key) => {
      newVisibility[key].visible = false;
    });

    // Enable only the layers specified in the slide's visibleObjects
    const visibleObjects = slides[index]?.content?.visibleObjects || [];
    visibleObjects.forEach((name) => {
      if (newVisibility[name]) {
        newVisibility[name].visible = true;
      }
    });

    setLayerVisibility(newVisibility);
  };

  const handleToggleScribbleMode = () => {
    setIsScribbleMode(!isScribbleMode);
  };

  const renderSlideContent = () => {
    const slide = slides[currentSlide];
    switch (slide.type) {
      case 'image':
        return <img src={slide.content} alt="Slide" className="max-w-full max-h-full mx-auto overflow-hidden" />;

      case 'video':
        return (
          <video controls className="max-w-full max-h-full mx-auto">
            <source src={slide.content} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        );

      case 'youtube':
        return (
          <iframe
            width="100%"
            height="100%"
            src={slide.content}
            title="YouTube video"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="max-w-full max-h-full mx-auto"
          ></iframe>
        );

      case 'scene':
        return null; // We render the Canvas separately for scene slides

      default:
        return null;
    }
  };


  //////////LAYER VISIBILITY////////////////
  
  const [layerVisibility, setLayerVisibility] = useState({}); // Manage visibility of each layer
  const [showLayerControl, setShowLayerControl] = useState(false); // Toggle for Layer Control visibility  
  const [showInfoControl, setShowInfoControl] = useState(false); // Toggle for Layer Control visibility
  const [showStopesControl, setShowStopesControl] = useState(false); // Toggle for Layer Control visibility
  
  useEffect(() => {
    const initialVisibility = {};
    ModelListBR.forEach((group) => {
      group.models.forEach((model) => {
        initialVisibility[model.name] = { visible: false, url: model.url };
      });
    });
    setLayerVisibility(initialVisibility);
  }, []);

   // Toggle visibility of layers from LayerControl
  const handleToggleVisibility = (modelName) => {
    setLayerVisibility((prevState) => ({
      ...prevState,
      [modelName]: {
        ...prevState[modelName],
        visible: !prevState[modelName].visible,
      }
    }));
  };

  // Handle keyboard shortcut for toggling layer control panel
  useEffect(() => {
    const handleToggleLayerControl = () => {
      setShowLayerControl(!showLayerControl);
    };

    window.addEventListener('toggleLayerControl', handleToggleLayerControl);

    return () => {
      window.removeEventListener('toggleLayerControl', handleToggleLayerControl);
    };
  }, [showLayerControl]);

  // Check if any block models or holes are visible
  const hasVisibleBlockModel = () => {
    return ModelListBR.some((group) =>
      group.models.some((model) => {
        if (model.type === 'blockmodel' || model.type === 'holes' || model.type === 'holesassays' || model.type === 'plannedholes') {
          const modelState = layerVisibility[model.name];
          return modelState && modelState.visible;
        }
        return false;
      })
    );
  };

  return (
    <div className="flex h-screen relative">
      {/* Sidebar */}
      <div className={`transition-all duration-300 bg-black text-white h-full flex flex-col ${isSidebarOpen ? 'w-48' : 'w-12'}`}>
        <div className="relative flex justify-center mb-1">
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-black p-1.5 rounded-full shadow-lg hover:from-yellow-400 hover:to-yellow-500 focus:outline-none transition-all duration-200 font-bold text-sm w-8 h-8 flex items-center justify-center"
          >
            {isSidebarOpen ? '←' : '→'}
          </button>
        </div>
        {isSidebarOpen ? (
          <div className='flex-1 overflow-y-auto p-1.5' >
            <h2 className="text-xs font-bold mb-2 text-center text-yellow-400 drop-shadow-lg tracking-wide">BLUEROCK</h2>
            <ul className="space-y-1">
              {slides.map((slide, index) => (
                <li
                key={index}
                className={`flex items-center p-1.5 cursor-pointer ${index === currentSlide ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-black' : 'bg-gray-700/80 hover:bg-gray-600/80'} transition-all rounded-md shadow-sm hover:shadow-md`}
                onClick={() => handleSlideClick(index)}
              >
                <div className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${index === currentSlide ? 'bg-yellow-300 text-black' : 'bg-gray-500 text-white'}`}>
                  {index + 1}
                </div>
                <span className="text-xs font-semibold ml-2 truncate">{slide.heading}</span>
              </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            <ul className="flex flex-col items-center space-y-2 mt-8 px-1" >
              {slides.map((_, index) => (
                <li
                  key={index}
                  className={`w-6 h-6 flex items-center justify-center cursor-pointer rounded-full text-xs font-bold ${index === currentSlide ? 'bg-yellow-400 text-black shadow-lg' : 'bg-gray-500 text-white hover:bg-gray-400 shadow-sm'}`}
                  onClick={() => handleSlideClick(index)}
                >
                  {index + 1}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-grow flex flex-col transition-all duration-300 relative overflow-auto">
      {(slides[currentSlide].type === 'scene' || slides[currentSlide].type === '360') && progress < 100 && (
          <div className="fixed inset-0 z-40 flex flex-col items-center justify-center bg-gray-900 bg-opacity-90">
            <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <h1 className="mt-4 text-white text-xl font-semibold">Loading ...</h1>
            <p className="text-white text-lg">{progress.toFixed(0)}%</p>
          </div>
        )}
        <div className="flex-grow flex items-center justify-center bg-gray-400 overflow-hidden">
          {renderSlideContent()}
        </div>
        {/* Description Box */}
        {showDescription && slides[currentSlide].content.description && (
          <div className="absolute bottom-14 left-0 w-full bg-gray-100 p-4 shadow-lg flex justify-between items-center text-gray-700 z-50">
            <p dangerouslySetInnerHTML={{ __html: slides[currentSlide].content.description }}></p>
            <button
              onClick={() => setShowDescription(false)}
              className="text-gray-500 hover:text-gray-700 font-bold text-sm w-fit"
            >
              &times;
            </button>
          </div>
        )}


        {slides[currentSlide].type === 'scene' && (
          <>

            {/* Toggle Button for Layer Control */}
             <button
              onClick={() => setShowLayerControl(!showLayerControl)}
              className = {`absolute top-16 right-4 ${showLayerControl ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-black' : 'bg-gray-500 text-white'} w-8 h-8 flex items-center justify-center rounded-full shadow-lg hover:from-yellow-400 hover:to-yellow-500 transition-all duration-200 z-50`}
              >
              🗂️
            </button>
           

            {/* Layer Control Panel */}
            {showLayerControl && (
              <div className="absolute top-0 left-0 w-full sm:w-80 bg-white border border-yellow-400 rounded-r-lg shadow-lg h-full max-h-screen z-40 overflow-hidden">
                <LayerControl
                  modelList={ModelListBR}
                  layerVisibility={layerVisibility}
                  onToggleVisibility={handleToggleVisibility}
                  onClose={() => setShowLayerControl(false)}
                />
              </div>
            )}

            {/* Toggle Button for Info Control */}
            <button
              onClick={() => setShowInfoControl(!showInfoControl)}
              className = {`absolute top-40 right-4 ${showInfoControl ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-black' : 'bg-gray-500 text-white'} w-8 h-8 flex items-center justify-center rounded-full shadow-lg hover:from-yellow-400 hover:to-yellow-500 transition-all duration-200 z-50`}
              >
                📊
            </button>
           {/* Layer Control Panel */}
           {showInfoControl && (
              
              <ObjectDetailsPage/>
              // <StopeStatistics/>
            )}

            

            {/* Toggle Button for Stopes Control */}
            <button
              onClick={() => setShowStopesControl(!showStopesControl)}
              className = {`absolute top-28 right-4 ${showStopesControl ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-black' : 'bg-gray-500 text-white'} w-8 h-8 flex items-center justify-center rounded-full shadow-lg hover:from-yellow-400 hover:to-yellow-500 transition-all duration-200 z-50`}
              >
                ⛏️
            </button>
            {/* Layer Control Panel */}
            {showStopesControl && (
              
              <StopeStatistics/>
            )}

       
            <Canvas className="absolute inset-0" flat shadows gl={{ antialias: true }} camera={{ position: [0, 7000, 0], near: 0.5, far: 10000, fov: 35 }}>
              <SceneComponent
                currentSlide = {currentSlide}
                layerVisibility={layerVisibility} 
                cameraPosition={slides[currentSlide].content.cameraPosition}
                lookAt={slides[currentSlide].content.lookAt}
                animateCamera={previousSlideWasScene}
                onThreeReady={setThree}
              />
            </Canvas>
            {/* Add the slicer component */}
            {three && <SceneSlicer three={three} />}

            
            <Measurements/>
            <ColorChange/>
            <ObjectDetails/>
            <BottomBarContainer showUnifiedPanel={showLayerControl} panelWidth="default"/>
            <BlockModelLegend isVisible={hasVisibleBlockModel()} />
            <Info/>
          </>



        )}

        {/* Scribble Toggle Button */}
        <button
          onClick={handleToggleScribbleMode}
          className="absolute top-4 right-4 bg-gray-500 text-white w-8 h-8 flex items-center justify-center rounded-full shadow-lg hover:bg-gray-600 z-50"
        >
          ✏️
        </button>

        {/* Scribble Component */}
        <Scribble
          isScribbleMode={isScribbleMode}
          selectedColor={selectedColor}
          onColorChange={setSelectedColor}
        />

        

        {/* Footer Navigation */}
        <div className="py-1 bg-blue-200 text-blue-900 flex justify-between items-center px-4">
          <button
            onClick={() => handleSlideClick((currentSlide - 1 + slides.length) % slides.length)}
            className="bg-blue-600 text-white w-fit px-3 py-1 md:px-4 md:py-2 rounded shadow-md hover:bg-blue-700"
          >
            Previous
          </button>
          <span className="text-center text-sm md:text-lg w-fit">
            <div className="w-fit p-2 h-5  md:h-12 flex items-center justify-center rounded-full shadow bg-gray-100 font-bold">
              {currentSlide + 1} / {slides.length}
            </div>
          </span>
          <button
            onClick={() => handleSlideClick((currentSlide + 1) % slides.length)}
            className="bg-blue-600 text-white w-fit px-3 py-1 md:px-4 md:py-2 rounded shadow-md hover:bg-blue-700"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default Presentation;
